import { type QueueTab, SuggestionQueue } from "@/components/analysis/suggestion-queue";
import { Button } from "@/components/ui/button";
import { ApiError, api } from "@/lib/api";
import { paginationSearchFields } from "@/lib/search";
import {
  Link,
  Navigate,
  createFileRoute,
  useLoaderData,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/suggestions/work-items` — **Work item suggestions** (admin-only). The
 * cross-source queue of everything the AI has proposed about TICKETS (meeting
 * transcripts, WhatsApp, forwarded client emails, the duplicate scan). Approve
 * a suggestion to apply it through the normal flow, or reject it; each shows
 * the source it's based on. Running a transcript and configuring the automated
 * sources live on `/analysis/sources`.
 *
 * Proposed TIME ENTRIES have their own page (`/suggestions/time-entries`) —
 * they're confirmed by the person whose time it is, not triaged by an admin.
 *
 * The queue is paged, and the tab, page and page size live in the URL like
 * every other list in Hub — a backlog worked through in passes keeps its place
 * across a reload, and "here's what I'm looking at" is a link.
 *
 * E2E behaviour contract lives in `e2e/suggestions/suggestions.spec.ts`.
 */

const searchSchema = z.object({
  status: z.enum(["pending", "approved", "rejected", "all"]).optional(),
  ...paginationSearchFields,
});

export const Route = createFileRoute("/_app/suggestions/work-items")({
  validateSearch: searchSchema,
  component: WorkItemSuggestionsPage,
});

function WorkItemSuggestionsPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const search = Route.useSearch();
  const navigate = useNavigate();
  const tab: QueueTab = search.status ?? "pending";
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  // The scan now runs async on the worker, so there's nothing to force-reload
  // right after triggering it — proposals appear once it finishes and the
  // admin revisits Pending. Kept as a stable key the queue can still key off.
  const [reloadKey] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState<string | null>(null);

  async function runDuplicateScan() {
    setScanning(true);
    setScanMsg(null);
    try {
      // The scan runs on the worker (it can take minutes over a big backlog).
      // We only enqueue it here and return — the result lands in Pending and
      // its progress/failure shows on the Sync page, so a slow scan can't make
      // the browser time out and wrongly report "failed".
      await api.post("/api/work-item-suggestions/scan-duplicates", {});
      setScanMsg(t("sug.workItems.scanStarted"));
    } catch (err) {
      const body =
        err instanceof ApiError && err.detail && typeof err.detail === "object"
          ? (err.detail as { error?: string; detail?: string })
          : undefined;
      const reason = body?.detail ?? body?.error;
      setScanMsg(
        reason ? t("sug.workItems.scanFailedReason", { reason }) : t("sug.workItems.scanFailed"),
      );
    } finally {
      setScanning(false);
    }
  }

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    api
      .get<{ projects: Array<{ id: string; name: string }> }>("/api/projects?pageSize=200")
      .then((res) => setProjects(res.projects.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {
        /* the create-item project picker just stays empty */
      });
  }, [user.globalRole]);

  if (user.globalRole !== "admin") {
    return <Navigate to="/" />;
  }

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">{t("sug.workItems.title")}</h1>
          <p className="text-muted-foreground mt-1">
            <Trans
              i18nKey="sug.workItems.intro"
              components={{
                link: <Link to="/suggestions/time-entries" className="underline" />,
              }}
            />
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-2">
            <Button asChild data-testid="review-one-by-one">
              <Link to="/suggestions/work-items/review">{t("sug.workItems.reviewOneByOne")}</Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={runDuplicateScan}
              disabled={scanning}
              data-testid="scan-duplicates"
            >
              {scanning ? t("sug.workItems.scanning") : t("sug.workItems.scanDuplicates")}
            </Button>
          </div>
          {scanMsg && <span className="text-xs text-muted-foreground">{scanMsg}</span>}
        </div>
      </div>

      <SuggestionQueue
        tab={tab}
        onTabChange={(t) =>
          navigate({
            to: "/suggestions/work-items",
            // A new tab is a new list — staying on page 4 of it would land on
            // an empty page as often as not.
            search: { ...search, status: t === "pending" ? undefined : t, page: undefined },
          })
        }
        reloadKey={reloadKey}
        projects={projects}
        page={search.page}
        pageSize={search.pageSize}
        basePath="/suggestions/work-items"
        searchParams={{ status: search.status, pageSize: search.pageSize }}
      />
    </div>
  );
}
